import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  TrendingUp, Users, CheckCircle, Leaf, Award, BarChart,
  ArrowRight, FileText, Download, AlertCircle
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import InvestorSubscriptionForm from '../components/forms/InvestorSubscriptionForm';

interface Campaign {
  id: string;
  name: string;
  target_amount: number;
  raised_amount: number;
  investor_count: number;
  minimum_ticket: number;
  investment_type: string;
  horizon_years: string;
  sector: string;
  fund_usage: string[];
  why_invest: Array<{
    icon: string;
    title: string;
    description: string;
  }>;
  legal_notice: string;
  partner_cabinet: string;
}

interface Document {
  id: string;
  title: string;
  description: string;
  file_url: string;
  file_type: string;
}

const iconMap: Record<string, React.ReactNode> = {
  TrendingUp: <TrendingUp className="w-6 h-6" />,
  Leaf: <Leaf className="w-6 h-6" />,
  CheckCircle: <CheckCircle className="w-6 h-6" />,
  Users: <Users className="w-6 h-6" />,
  Award: <Award className="w-6 h-6" />,
  BarChart: <BarChart className="w-6 h-6" />,
};

const Invest: React.FC = () => {
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Investir dans Proxy | Proxy Emobility';
    fetchCampaignData();
  }, []);

  const fetchCampaignData = async () => {
    try {
      const { data: campaignData, error: campaignError } = await supabase
        .from('fundraising_campaigns')
        .select('*')
        .eq('is_active', true)
        .single();

      if (campaignError) throw campaignError;

      setCampaign(campaignData as Campaign);

      const { data: docsData } = await supabase
        .from('fundraising_documents')
        .select('*')
        .eq('campaign_id', campaignData.id)
        .eq('is_public', true)
        .order('display_order');

      setDocuments((docsData as Document[]) || []);
    } catch (error) {
      console.error('Error fetching campaign data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const calculatePercentage = () => {
    if (!campaign) return 0;
    return Math.min((campaign.raised_amount / campaign.target_amount) * 100, 100);
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle size={48} className="text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Aucune campagne active pour le moment.</p>
        </div>
      </div>
    );
  }

  const percentage = calculatePercentage();

  return (
    <div className="pt-20">
      <Helmet>
        <title>Investir dans Proxy | Proxy Emobility - Levée de Fonds</title>
        <meta name="description" content="Investissez dans la mobilité électrique en Afrique. Première levée de fonds Proxy Africa de 500 000 €, encadrée par un cabinet expert en fundraising." />
        <meta name="keywords" content="investir, Proxy Africa, levée de fonds, mobilité électrique, Afrique, investissement, fundraising" />
        <link rel="canonical" href="https://proxyemobility.netlify.app/invest" />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,_rgba(34,197,94,0.15)_0%,_transparent_50%)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_70%,_rgba(34,197,94,0.1)_0%,_transparent_50%)]"></div>

        <div className="container mx-auto px-4 md:px-6 relative">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block mb-6 px-4 py-2 bg-primary-500/20 border border-primary-500/30 rounded-full text-sm font-medium text-primary-300">
              Levée encadrée par {campaign.partner_cabinet}
            </div>

            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              {campaign.name}
            </h1>

            <p className="text-xl md:text-2xl text-gray-300 mb-8 leading-relaxed">
              Investissez dans la mobilité électrique et les infrastructures durables en Afrique
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#subscribe"
                className="px-8 py-4 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-xl transition-all shadow-lg hover:shadow-xl transform hover:scale-105 inline-flex items-center justify-center"
              >
                Souscrire maintenant
                <ArrowRight size={20} className="ml-2" />
              </a>
              <a
                href="#details"
                className="px-8 py-4 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white font-semibold rounded-xl transition-all border border-white/30 inline-flex items-center justify-center"
              >
                En savoir plus
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Campaign Progress */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-5xl mx-auto">
            <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-xl p-8 border border-gray-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
                Progression de la levée
              </h2>

              <div className="mb-6">
                <div className="flex justify-between mb-2">
                  <span className="text-sm font-medium text-gray-600">Montant levé</span>
                  <span className="text-sm font-bold text-primary-600">{percentage.toFixed(1)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-primary-500 to-primary-600 h-4 rounded-full transition-all duration-1000 ease-out relative overflow-hidden"
                    style={{ width: `${percentage}%` }}
                  >
                    <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                <div className="text-center p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                  <div className="text-3xl md:text-4xl font-bold text-gray-900 mb-1">
                    {formatCurrency(campaign.raised_amount)}
                  </div>
                  <div className="text-sm text-gray-600">Montant levé</div>
                </div>

                <div className="text-center p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                  <div className="text-3xl md:text-4xl font-bold text-gray-900 mb-1">
                    {formatCurrency(campaign.target_amount)}
                  </div>
                  <div className="text-sm text-gray-600">Objectif</div>
                </div>

                <div className="text-center p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                  <div className="text-3xl md:text-4xl font-bold text-gray-900 mb-1">
                    {campaign.investor_count}
                  </div>
                  <div className="text-sm text-gray-600">Investisseurs</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Campaign Details */}
      <section id="details" className="py-16 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Détails de la levée
              </h2>
              <p className="text-lg text-gray-600">
                Une opportunité d'investir dans un secteur en pleine croissance
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
              <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
                <div className="text-sm text-gray-600 mb-1">Montant cible</div>
                <div className="text-2xl font-bold text-gray-900">{formatCurrency(campaign.target_amount)}</div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
                <div className="text-sm text-gray-600 mb-1">Ticket minimum</div>
                <div className="text-2xl font-bold text-gray-900">{formatCurrency(campaign.minimum_ticket)}</div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
                <div className="text-sm text-gray-600 mb-1">Type d'investissement</div>
                <div className="text-2xl font-bold text-gray-900">{campaign.investment_type}</div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
                <div className="text-sm text-gray-600 mb-1">Horizon</div>
                <div className="text-2xl font-bold text-gray-900">{campaign.horizon_years}</div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100 mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Secteur</h3>
              <p className="text-gray-700 text-lg">{campaign.sector}</p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Utilisation des fonds</h3>
              <div className="space-y-3">
                {campaign.fund_usage.map((item, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle size={20} className="text-primary-600 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Invest */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Pourquoi investir ?
            </h2>
            <p className="text-lg text-gray-600">
              Une opportunité unique d'investir dans un secteur à fort potentiel avec un impact positif
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {campaign.why_invest.map((reason, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all hover:border-primary-200 group"
              >
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4 text-primary-600 group-hover:bg-primary-600 group-hover:text-white transition-colors">
                  {iconMap[reason.icon] || <CheckCircle className="w-6 h-6" />}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{reason.title}</h3>
                <p className="text-gray-600">{reason.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Documents Section */}
      {documents.length > 0 && (
        <section className="py-16 bg-gradient-to-b from-white to-gray-50">
          <div className="container mx-auto px-4 md:px-6">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Documents investisseurs
              </h2>
              <p className="text-lg text-gray-600">
                Téléchargez notre documentation pour en savoir plus
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {documents.map((doc) => (
                <div key={doc.id} className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all hover:border-primary-200">
                  <div className="flex items-center mb-4">
                    <FileText size={24} className="text-primary-600 mr-3" />
                    <h3 className="font-bold text-gray-900">{doc.title}</h3>
                  </div>
                  <p className="text-gray-600 text-sm mb-4">{doc.description}</p>
                  <a
                    href={doc.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium text-sm"
                  >
                    <Download size={16} className="mr-1" />
                    Télécharger ({doc.file_type})
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Subscription Form */}
      <section id="subscribe" className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Souscrire à la levée de fonds
              </h2>
              <p className="text-lg text-gray-600">
                Remplissez le formulaire ci-dessous pour manifester votre intérêt. Notre équipe vous contactera dans les plus brefs délais.
              </p>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-xl p-8 border border-gray-100">
              <InvestorSubscriptionForm campaignId={campaign.id} />
            </div>

            {/* Legal Notice */}
            <div className="mt-8 p-6 bg-amber-50 border border-amber-200 rounded-xl">
              <div className="flex items-start">
                <AlertCircle size={20} className="text-amber-600 mr-3 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-900">
                  {campaign.legal_notice}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Invest;
